import React, { useState, useEffect } from "react";
import { NavLink, Link as RouterLink, useLocation } from "react-router-dom";
import { Link as ScrollLink, scroller } from "react-scroll";
import { injectIntl, FormattedMessage } from "react-intl";
import styles from "./Header.module.css";
import logoVertex from "./assets/images/logo_nova_4d_azul.png";
import LanguageSelector from "components/language-selector";

const Header = ({ intl }) => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const toggleMobileMenu = () => setMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const onHomePage = location.pathname === "/";

  const navLinks = [
    {
      type: onHomePage ? "scroll" : "page",
      to: onHomePage ? "inicio" : "/",
      label: <FormattedMessage id="header.nav.home" defaultMessage="Início" />
    },
    {
      type: onHomePage ? "scroll" : "page",
      to: onHomePage ? "como-funciona" : "/#como-funciona",
      label: <FormattedMessage id="header.nav.howItWorks" defaultMessage="Como funciona" />
    },
    {
      type: onHomePage ? "scroll" : "page",
      to: onHomePage ? "sobre" : "/sobre",
      label: <FormattedMessage id="header.nav.about" defaultMessage="Sobre" />
    },
    {
      type: onHomePage ? "scroll" : "page",
      to: onHomePage ? "relatorios-ia" : "/#relatorios-ia",
      label: <FormattedMessage id="header.nav.differentials" defaultMessage="Diferenciais" />
    },
    {
      type: onHomePage ? "scroll" : "page",
      to: onHomePage ? "casos-uso" : "/#casos-uso",
      label: <FormattedMessage id="header.nav.useCases" defaultMessage="Casos de uso" />
    }
  ];

  const scrollProps = {
    spy: true,
    smooth: "easeInOutQuad",
    offset: -76,
    duration: 500,
    onClick: closeMobileMenu
  };

  const handleLogoClick = e => {
    if (onHomePage) {
      e.preventDefault();
      scroller.scrollTo("inicio", { ...scrollProps, duration: 800 });
    }
  };

  const renderNavLink = (link, index) => {
    if (link.type === "page") {
      return (
        <NavLink
          key={index}
          to={link.to}
          className={styles.navLink}
          activeClassName={styles.active}
          onClick={closeMobileMenu}
          exact
        >
          {link.label}
        </NavLink>
      );
    }
    return (
      <ScrollLink
        key={index}
        to={link.to}
        {...scrollProps}
        className={styles.navLink}
        activeClass={styles.active}
      >
        {link.label}
      </ScrollLink>
    );
  };

  const handleLanguageChange = () => {
    if (isMobile) {
      closeMobileMenu();
    }
  };

  return (
    <>
      <header
        className={`${styles.navbar} ${isScrolled ? styles.scrolled : ""} ${
          isMobileMenuOpen ? styles.mobileMenuOpen : ""
        }`}
      >
        <div className={styles.navContainer}>
          <RouterLink
            to="/"
            onClick={handleLogoClick}
            className={styles.navBrand}
            aria-label={intl.formatMessage({ id: "header.aria.home", defaultMessage: "Página inicial Vertex4D" })}
          >
            <img
              src={logoVertex}
              alt="Logo Vertex4D"
              className={styles.logo}
            />
            <span className={styles.brandText}>Vertex4D</span>
          </RouterLink>

          <nav className={styles.navMenu}>
            {navLinks.map((link, index) => renderNavLink(link, index))}
            <a
              href="/login"
              className={styles.platformBtn}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage id="header.nav.login" defaultMessage="Entrar" />
            </a>
          </nav>

          <LanguageSelector
            isMobile={false}
            onLanguageChange={handleLanguageChange}
          />

          <button
            className={`${styles.mobileMenuToggle} ${
              isMobileMenuOpen ? styles.active : ""
            }`}
            aria-label={intl.formatMessage({ id: "header.aria.openMenu", defaultMessage: "Abrir menu" })}
            onClick={toggleMobileMenu}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      <div
        className={`${styles.mobileOverlay} ${
          isMobileMenuOpen ? styles.active : ""
        }`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      ></div>

      <aside
        className={`${styles.mobileSidebar} ${
          isMobileMenuOpen ? styles.active : ""
        }`}
      >
        <div className={styles.mobileSidebarHeader}>
          <img
            src={logoVertex}
            alt="Logo Vertex4D"
            className={styles.logo}
          />
          <span className={styles.brandText}>Vertex4D</span>
        </div>
        <nav className={styles.mobileSidebarNav}>
          {navLinks.map((link, index) => renderNavLink(link, index))}
          <div className={styles.mobileLanguageSelector}>
            <LanguageSelector
              isMobile={true}
              isMobileMenuOpen={isMobileMenuOpen}
              onLanguageChange={handleLanguageChange}
            />
          </div>
          <a
            href="/login"
            className={styles.platformBtn}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedMessage id="header.nav.login" />
          </a>
        </nav>
      </aside>
    </>
  );
};

export default injectIntl(Header);

